import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { canDeleteStatus } from '../../FlowchartView/utils/statusUtils';
import DeleteColumnModal from './DeleteColumnModal';
import AddItemPopup from './KanbanColumn/AddItemPopup';

/**
 * SortableHeaderColumn - Individual sortable column header
 */
const SortableHeaderColumn = ({ 
  column, 
  columnIndex,
  isCollapsed, 
  columnCount,
  currentSwimlaneLabel,
  activeColumnId,
  onToggleCollapse,
  onDeleteClick,
  onAddClick,
  isAddPopupOpen,
  onCloseAddPopup,
  onAddMission,
  onAddDecision,
  onAddWait,
  onColumnNameSave,
}) => {
  const addButtonRef = useRef(null);
  const nameInputRef = useRef(null);
  const nameLabelRef = useRef(null);
  const clickCursorPosRef = useRef(null);
  
  // Local state for name editing
  const [editValue, setEditValue] = useState(column.name);
  const [isEditing, setIsEditing] = useState(false);
  
  // FLIP animation: track previous index to calculate animation offset
  // This syncs with SwimlaneColumnCell and CollapsedSlot animations
  const prevIndexRef = useRef(columnIndex);
  const [animationOffset, setAnimationOffset] = useState(0);
  
  useLayoutEffect(() => {
    if (prevIndexRef.current !== columnIndex && activeColumnId) {
      const indexDelta = prevIndexRef.current - columnIndex;
      // Match column width: 320px base + 16px margin (8px each side)
      const columnWidth = isCollapsed ? 56 : 336; // 40px + 16px for collapsed, 320px + 16px for expanded
      const offset = indexDelta * columnWidth;
      
      // Invert
      setAnimationOffset(offset);
      
      // Play
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationOffset(0);
        });
      });
    }
    prevIndexRef.current = columnIndex;
  }, [columnIndex, activeColumnId, isCollapsed]);
  
  // Focus input when editing starts and set cursor position from click
  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      nameInputRef.current.focus();
      // Set cursor position from click, or end if not available
      const pos = clickCursorPosRef.current ?? editValue.length;
      nameInputRef.current.setSelectionRange(pos, pos);
      clickCursorPosRef.current = null; // Reset for next time
    }
  }, [isEditing, editValue.length]);

  // Update edit value when column name changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(column.name);
    }
  }, [column.name, isEditing]);

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditing(false);
      onColumnNameSave?.(column.id, editValue);
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(column.name);
    }
  };

  const handleNameBlur = () => {
    setIsEditing(false);
    onColumnNameSave?.(column.id, editValue);
  };

  const handleLabelClick = (e) => {
    e.stopPropagation();
    
    // Calculate cursor position from click location (like Notion)
    if (nameLabelRef.current && document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range && nameLabelRef.current.contains(range.startContainer)) {
        clickCursorPosRef.current = range.startOffset;
      } else {
        clickCursorPosRef.current = column.name.length; // Default to end
      }
    } else {
      clickCursorPosRef.current = column.name.length;
    }
    
    setIsEditing(true);
  };
  
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ 
    id: column.id,
    data: {
      type: 'column',
      columnId: column.id,
    },
  });

  // Use FLIP animation instead of dnd-kit transforms for sync with body columns
  const style = {
    transform: animationOffset !== 0 ? `translateX(${animationOffset}px)` : undefined,
    transition: animationOffset === 0 && activeColumnId ? 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
    zIndex: isDragging ? 1000 : 'auto',
  };

  if (isCollapsed) {
    // Collapsed columns render a narrow draggable spacer in the header
    return (
      <div 
        ref={setNodeRef}
        style={style}
        className={`kanban-swimlanes__header-column kanban-swimlanes__header-column--collapsed-spacer ${isDragging ? 'kanban-swimlanes__header-column--dragging' : ''}`}
        {...attributes}
        {...listeners}
      >
        {/* Placeholder shown when dragging (connects to swimlane row placeholder) */}
        {isDragging && <div className="kanban-swimlanes__header-placeholder" />}
        {/* Drag handle for collapsed state - visible on hover, hidden when dragging */}
        {!isDragging && (
          <span className="kanban-swimlanes__header-drag-handle kanban-swimlanes__header-drag-handle--collapsed" title="Drag to reorder">
            <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
              <circle cx="2" cy="2" r="1.5" />
              <circle cx="8" cy="2" r="1.5" />
              <circle cx="2" cy="7" r="1.5" />
              <circle cx="8" cy="7" r="1.5" />
              <circle cx="2" cy="12" r="1.5" />
              <circle cx="8" cy="12" r="1.5" />
            </svg>
          </span>
        )}
      </div>
    );
  }

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`kanban-swimlanes__header-column ${isDragging ? 'kanban-swimlanes__header-column--dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Placeholder shown when dragging */}
      {isDragging && <div className="kanban-swimlanes__header-placeholder" />}
      
      {/* Drag handle (visual indicator only - whole header is draggable) */}
      <span 
        className="kanban-swimlanes__header-drag-handle" 
        title="Drag to reorder"
      >
        <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
          <circle cx="2" cy="2" r="1.5" />
          <circle cx="8" cy="2" r="1.5" />
          <circle cx="2" cy="7" r="1.5" />
          <circle cx="8" cy="7" r="1.5" />
          <circle cx="2" cy="12" r="1.5" />
          <circle cx="8" cy="12" r="1.5" />
        </svg>
      </span>
      <span 
        className="kanban-swimlanes__header-color"
        style={{ backgroundColor: column.color }}
      />
      {isEditing ? (
        <input
          ref={nameInputRef}
          type="text"
          className="kanban-swimlanes__header-label-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            handleNameKeyDown(e);
          }}
          onBlur={handleNameBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <span 
          ref={nameLabelRef}
          className="kanban-swimlanes__header-label"
          onClick={handleLabelClick}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          title="Click to rename"
        >
          {column.name}
        </span>
      )}
      <span className="kanban-swimlanes__header-count">{columnCount}</span>
      
      {/* Action buttons - pushed to right */}
      <div className="kanban-swimlanes__header-actions">
        {/* Collapse button */}
        <button 
          className="kanban-swimlanes__header-btn kanban-swimlanes__header-btn--collapse"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse?.(column.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          title="Collapse column"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="11 17 6 12 11 7" />
            <polyline points="18 17 13 12 18 7" />
          </svg>
        </button>
        
        {/* Delete button */}
        <button 
          className="kanban-swimlanes__header-btn kanban-swimlanes__header-btn--delete"
          onClick={(e) => onDeleteClick(e, column)}
          onPointerDown={(e) => e.stopPropagation()}
          title="Delete column"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
        
        {/* Add item button */}
        <div className="kanban-swimlanes__header-add-wrapper">
          <button 
            ref={addButtonRef}
            className="kanban-swimlanes__header-btn kanban-swimlanes__header-btn--add"
            onClick={(e) => onAddClick(e, column.id)}
            onPointerDown={(e) => e.stopPropagation()}
            title={currentSwimlaneLabel ? `Add item to "${currentSwimlaneLabel}"` : 'Add item'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <AddItemPopup
            isOpen={isAddPopupOpen}
            onClose={onCloseAddPopup}
            buttonRef={addButtonRef}
            swimlaneLabel={currentSwimlaneLabel}
            onAddMission={onAddMission}
            onAddDecision={onAddDecision}
            onAddWait={onAddWait}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * SwimlaneStickyHeader Component
 * 
 * Renders the sticky column headers for swimlane view with:
 * - Drag handle for reordering columns
 * - Collapse button
 * - Delete button  
 * - Add item button (+ Mission/Decision)
 * 
 * The add button shows which swimlane the item will be added to
 * based on which swimlane is currently "in view" (touching the headers)
 */
const SwimlaneStickyHeader = ({
  columns,
  collapsedColumns,
  tasksByStatus,
  statuses,
  swimlanes,
  currentSwimlaneId,
  activeColumnId,
  onToggleCollapse,
  onDeleteColumn,
  onAddStatus,
  onReassignTasksStatus,
  onAddTask,
  onAddDecision,
  onAddWait,
  onSetEditingCardId,
  onRenameStatus,
}) => {
  // State for delete modal
  const [deleteModalColumn, setDeleteModalColumn] = useState(null);
  
  // State for add popup - track which column's popup is open
  const [openAddPopupColumnId, setOpenAddPopupColumnId] = useState(null);

  // Get the current swimlane label for display
  const currentSwimlane = swimlanes.find(s => s.id === currentSwimlaneId);
  const currentSwimlaneLabel = currentSwimlane?.label || null;

  // Handle delete button click
  const handleDeleteClick = (e, column) => {
    e.stopPropagation();
    const deleteCheck = canDeleteStatus(column.id, statuses);
    if (!deleteCheck.canDelete) {
      alert(deleteCheck.reason);
      return;
    }
    setDeleteModalColumn(column);
  };

  // Handle add button click
  const handleAddClick = (e, columnId) => {
    e.stopPropagation();
    setOpenAddPopupColumnId(openAddPopupColumnId === columnId ? null : columnId);
  };

  // Handle adding a mission
  const handleAddMission = async (columnId) => {
    if (currentSwimlaneId && onAddTask) {
      const newTaskId = await onAddTask(columnId, currentSwimlaneId);
      if (newTaskId) {
        onSetEditingCardId?.(newTaskId);
      }
    }
  };

  // Handle adding a decision
  const handleAddDecision = async (columnId) => {
    if (currentSwimlaneId && onAddDecision) {
      const newTaskId = await onAddDecision(columnId, currentSwimlaneId);
      if (newTaskId) {
        onSetEditingCardId?.(newTaskId);
      }
    }
  };

  const handleAddWait = async (columnId) => {
    if (currentSwimlaneId && onAddWait) {
      const newTaskId = await onAddWait(columnId, currentSwimlaneId);
      if (newTaskId) {
        onSetEditingCardId?.(newTaskId);
      }
    }
  };

  // Handle column name save
  const handleColumnNameSave = (columnId, newName) => {
    if (newName.trim()) {
      onRenameStatus?.(columnId, newName.trim());
    }
  };

  return (
    <>
      <div className="kanban-swimlanes__header">
        <div className="kanban-swimlanes__header-spacer" />
        {columns.map((column, index) => {
          const isCollapsed = collapsedColumns.has(column.id);
          const columnCount = (tasksByStatus[column.id] || []).length;
          
          return (
            <SortableHeaderColumn
              key={column.id}
              column={column}
              columnIndex={index}
              isCollapsed={isCollapsed}
              columnCount={columnCount}
              currentSwimlaneLabel={currentSwimlaneLabel}
              activeColumnId={activeColumnId}
              onToggleCollapse={onToggleCollapse}
              onDeleteClick={handleDeleteClick}
              onAddClick={handleAddClick}
              isAddPopupOpen={openAddPopupColumnId === column.id}
              onCloseAddPopup={() => setOpenAddPopupColumnId(null)}
              onAddMission={() => handleAddMission(column.id)}
              onAddDecision={() => handleAddDecision(column.id)}
              onAddWait={() => handleAddWait(column.id)}
              onColumnNameSave={handleColumnNameSave}
            />
          );
        })}
      </div>

      {/* Delete Column Modal */}
      {deleteModalColumn && (
        <DeleteColumnModal
          isOpen={!!deleteModalColumn}
          columnName={deleteModalColumn.name}
          columnId={deleteModalColumn.id}
          taskCount={(tasksByStatus[deleteModalColumn.id] || []).length}
          statuses={statuses}
          onCancel={() => setDeleteModalColumn(null)}
          onConfirm={({ targetStatusId, newStatus }) => {
            const columnId = deleteModalColumn.id;
            const tasks = tasksByStatus[columnId] || [];
            setDeleteModalColumn(null);
            
            // If creating a new status, add it first
            if (newStatus) {
              onAddStatus?.(newStatus);
            }
            
            // Reassign tasks if a target status was selected
            if (targetStatusId && tasks.length > 0) {
              onReassignTasksStatus?.(tasks.map(t => t.id), targetStatusId);
            }
            
            // Delete the column
            onDeleteColumn?.(columnId);
          }}
        />
      )}
    </>
  );
};

export default SwimlaneStickyHeader;

